import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, TrendingUp, Users, GraduationCap, ShieldCheck, Briefcase, IndianRupee, Award, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";

const Stat = ({ label, value, sub }) => (
  <div className="hair-border bg-white/[0.02] p-4"><p className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</p><p className="font-display text-2xl mt-1 gold-text">{value}</p>{sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}</div>
);
const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-8"><p className="errah-label gold-text mb-3 flex items-center gap-2"><Icon size={15} /> {title}</p>{children}</div>
);
const Bars = ({ data, money }) => {
  const entries = Array.isArray(data) ? data : Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return <div className="space-y-1.5">{entries.length === 0 ? <p className="text-xs text-gray-600">No data</p> : entries.map(([k, v]) => (
    <div key={k} className="flex items-center gap-3 text-xs"><span className="w-40 truncate text-gray-400">{k}</span>
      <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-[#C9A45C]" style={{ width: `${v / max * 100}%` }} /></div>
      <span className="w-20 text-right">{money ? `₹${(v || 0).toLocaleString()}` : v}</span></div>
  ))}</div>;
};

export default function AdminAnalytics() {
  const [d, setD] = useState(null);
  const [expiring, setExpiring] = useState(null);
  const [f, setF] = useState({ date_from: "", date_to: "", service: "", city: "", cert_status: "" });

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/admin/analytics", { params }).then(({ data }) => setD(data)).catch(() => {});
    api.get("/admin/certifications/expiring", { params: { days: 60, service: f.service || undefined, city: f.city || undefined } }).then(({ data }) => setExpiring(data)).catch(() => {});
  }, [f]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="min-h-screen bg-[#080808] flex items-center justify-center text-gray-500">Loading analytics…</div>;

  const af = d.application_funnel, tf = d.training_funnel, ct = d.certification, jb = d.jobs, rv = d.revenue, pf = d.performance;
  const inr = (n) => `₹${(n || 0).toLocaleString()}`;

  return (
    <div className="min-h-screen bg-[#080808]" data-testid="admin-analytics-page">
      <Seo title="Admin · Applicator Analytics" description="ERRĀH Applicator Network analytics" />
      <div className="glass"><div className="max-w-[1200px] mx-auto px-5 h-[60px] flex items-center gap-4">
        <Link to="/admin/applicators" className="flex items-center gap-2 text-sm text-gray-300"><ArrowLeft size={16} /> Applicators</Link>
        <Link to="/admin/training" className="text-sm text-[#C9A45C]">Training →</Link>
      </div></div>

      <div className="max-w-[1200px] mx-auto px-5 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="serif-head text-3xl lg:text-4xl">Applicator Network Analytics</h1>
          <button onClick={load} className="inline-flex items-center gap-1.5 border border-white/20 px-3 py-2 text-xs hover:bg-white hover:text-black transition-colors"><RefreshCw size={13} /> Refresh</button>
        </div>

        <div className="hair-border bg-white/[0.02] p-4 mb-8 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="analytics-filters">
          {[["date_from", "From", "date"], ["date_to", "To", "date"], ["city", "City", "text"]].map(([k, l, ty]) => (
            <div key={k}><label className="errah-label block mb-1">{l}</label><input type={ty} value={f[k]} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} data-testid={`filter-${k}`} className="w-full bg-transparent border border-white/15 px-2 py-2 text-xs" /></div>
          ))}
          <div><label className="errah-label block mb-1">Service</label>
            <select value={f.service} onChange={(e) => setF((s) => ({ ...s, service: e.target.value }))} data-testid="filter-service" className="w-full bg-[#111] border border-white/15 px-2 py-2 text-xs">
              <option value="">All</option>{(d.services || []).map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
          <div><label className="errah-label block mb-1">Cert Status</label>
            <select value={f.cert_status} onChange={(e) => setF((s) => ({ ...s, cert_status: e.target.value }))} data-testid="filter-cert-status" className="w-full bg-[#111] border border-white/15 px-2 py-2 text-xs">
              <option value="">All</option>{["CERTIFIED", "AWAITING CERTIFICATION", "ASSESSMENT PENDING", "TRAINING IN PROGRESS", "EXPIRED"].map((s) => <option key={s} value={s}>{s}</option>)}</select></div>
        </div>

        <Section icon={Users} title="Application Funnel">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="funnel-application">
            <Stat label="Received" value={af.received} /><Stat label="Approved" value={af.approved} /><Stat label="Rejected" value={af.rejected} /><Stat label="Suspended" value={af.suspended} /><Stat label="Approval Rate" value={`${af.approval_rate}%`} />
          </div>
        </Section>

        <Section icon={GraduationCap} title="Training Funnel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="funnel-training">
            <Stat label="Enrolled" value={tf.enrolled} /><Stat label="In Training" value={tf.in_training} /><Stat label="Training Completed" value={tf.training_completed} /><Stat label="Assessment Pending" value={tf.assessment_pending} />
            <Stat label="Assessment Passed" value={tf.assessment_passed} /><Stat label="Assessment Failed" value={tf.assessment_failed} /><Stat label="Certification Pending" value={tf.certification_pending} /><Stat label="Certified" value={tf.certified} />
          </div>
        </Section>

        <Section icon={ShieldCheck} title="Certification">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="cert-stats">
            <Stat label="Total Certified" value={ct.total_certified_applicators} /><Stat label="Expiring (60d)" value={ct.expiring} /><Stat label="Expired" value={ct.expired} /><Stat label="Active Certified" value={ct.active_certified} /><Stat label="Inactive" value={ct.inactive_certified} />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div><p className="text-xs text-gray-500 mb-2">By Service</p><Bars data={ct.by_service} /></div>
            <div><p className="text-xs text-gray-500 mb-2">By City</p><Bars data={ct.by_city} /></div>
          </div>
        </Section>

        {expiring && (expiring.expiring.length > 0 || expiring.expired.length > 0) && (
          <Section icon={AlertTriangle} title="Expiring & Expired Certifications">
            <div className="hair-border bg-white/[0.02] overflow-hidden" data-testid="expiring-table">
              {[...expiring.expiring, ...expiring.expired].slice(0, 40).map((c) => (
                <div key={c.certificate_id} className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 text-sm">
                  <span className="text-gray-300">{c.applicator_name} · {c.service_name}</span>
                  <span className={c.days_remaining < 0 ? "text-red-400 text-xs" : "text-[#E4C987] text-xs"}>{c.days_remaining < 0 ? `Expired ${-c.days_remaining}d ago` : `${c.days_remaining}d left`}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section icon={Briefcase} title="Jobs">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="jobs-stats">
            <Stat label="Created" value={jb.created} /><Stat label="Accepted" value={jb.accepted} /><Stat label="Declined" value={jb.declined} /><Stat label="Referred" value={jb.referred} />
            <Stat label="Completed" value={jb.completed} /><Stat label="Cancelled" value={jb.cancelled} /><Stat label="Unassigned" value={jb.unassigned} />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div><p className="text-xs text-gray-500 mb-2">By Service</p><Bars data={jb.by_service} /></div>
            <div><p className="text-xs text-gray-500 mb-2">By City</p><Bars data={jb.by_city} /></div>
          </div>
        </Section>

        <Section icon={IndianRupee} title="Revenue & Earnings">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="revenue-stats">
            <Stat label="Gross Business Value" value={inr(rv.gross_business_value)} /><Stat label="Installation Revenue" value={inr(rv.installation_revenue)} /><Stat label="Referral Revenue" value={inr(rv.referral_revenue)} /><Stat label="Network Revenue" value={inr(rv.network_revenue)} />
            <Stat label="Applicator Earnings" value={inr(rv.applicator_earnings)} /><Stat label="Network Commissions" value={inr(rv.network_commissions)} /><Stat label="Performance Rewards" value={inr(rv.performance_rewards)} />
            <Stat label="Pending" value={inr(rv.pending)} /><Stat label="Payable" value={inr(rv.payable)} /><Stat label="Paid" value={inr(rv.paid)} />
          </div>
        </Section>

        <Section icon={TrendingUp} title="Performance">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="performance-stats">
            <Stat label="Completion Rate" value={`${pf.completion_rate}%`} /><Stat label="Avg Rating" value={pf.avg_customer_rating || "—"} /><Stat label="QC Pass Rate" value={`${pf.qc_pass_rate}%`} /><Stat label="Repeat Customers" value={pf.repeat_customers} /><Stat label="Avg Job Value" value={inr(pf.avg_job_value)} />
          </div>
          <div><p className="text-xs text-gray-500 mb-2 flex items-center gap-1.5"><Award size={13} /> Top Services</p><Bars data={pf.top_services} /></div>
        </Section>
      </div>
    </div>
  );
}
